import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Newspaper, BookOpen, PenLine } from "lucide-react";
import { api } from "../lib/api";
import { Container } from "../components/ui-kit";

const FILTERS = [["All", "All"], ["News", "News"], ["Stories", "Story"], ["Blog", "Blog"]];
const ICON = { News: Newspaper, Story: BookOpen, Blog: PenLine };

function fmt(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function Media() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => (filter === "All" ? items : items.filter((m) => m.category === filter)), [items, filter]);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="media-page">
      <section className="relative overflow-hidden splash-bg py-20 sm:py-24">
        <div className="si-bloom pointer-events-none absolute left-1/2 top-0 h-[380px] w-[380px] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,154,46,0.18) 0%, transparent 62%)" }} />
        <Container className="relative text-center text-white">
          <div className="inline-flex items-center gap-2.5">
            <span className="h-px w-8 bg-gold" />
            <span className="text-xs font-semibold uppercase tracking-[0.26em] text-gold-light">Media &amp; Stories</span>
            <span className="h-px w-8 bg-gold" />
          </div>
          <h1 className="mt-5 font-display text-4xl font-bold sm:text-5xl lg:text-6xl">Our Work, News &amp; Reflections</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-emerald-50/80 sm:text-lg">
            Announcements, field stories and original writing on giving, technology and lasting impact.
          </p>
        </Container>
      </section>

      <section className="bg-sand">
        <Container className="py-14 lg:py-20">
          <div className="flex flex-wrap gap-2" data-testid="media-filters">
            {FILTERS.map(([label, val]) => (
              <button key={val} data-testid={`media-filter-${val.toLowerCase()}`} onClick={() => setFilter(val)}
                className={`rounded-full border px-5 py-2 text-sm font-semibold transition ${filter === val ? "border-emerald-900 bg-emerald-900 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-gold hover:text-gold"}`}>
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-800" /></div>
          ) : (
            <>
              {featured && (
                <Link to={`/media/${featured.slug}`} data-testid={`media-featured-${featured.slug}`}
                  className="group mt-10 grid overflow-hidden rounded-3xl border border-emerald-900/10 bg-white hh-shadow md:grid-cols-2">
                  <div className="relative h-60 overflow-hidden md:h-auto">
                    <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                    <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">{featured.category}</span>
                  </div>
                  <div className="flex flex-col justify-center p-7 sm:p-10">
                    <div className="text-xs font-medium text-slate-400">{fmt(featured.date)} · {featured.read_time}</div>
                    <h2 className="mt-3 font-display text-2xl font-bold leading-tight text-emerald-950 sm:text-3xl">{featured.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{featured.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-gold">Read More <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" /></span>
                  </div>
                </Link>
              )}

              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((m, i) => {
                  const Icon = ICON[m.category] || Newspaper;
                  return (
                    <motion.div key={m.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05, duration: 0.5 }}>
                      <Link to={`/media/${m.slug}`} data-testid={`media-card-${m.slug}`}
                        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-emerald-900/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:hh-shadow">
                        <div className="relative h-48 overflow-hidden">
                          <img src={m.image} alt={m.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-emerald-900"><Icon className="h-3.5 w-3.5" /> {m.category}</span>
                        </div>
                        <div className="flex flex-1 flex-col p-6">
                          <div className="text-xs font-medium text-slate-400">{fmt(m.date)} · {m.read_time}</div>
                          <h3 className="mt-2 font-display text-xl font-bold leading-snug text-emerald-950">{m.title}</h3>
                          <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{m.excerpt}</p>
                          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-gold">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" /></span>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </Container>
      </section>
    </div>
  );
}
